import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ApiService from '../../../../services/ApiService';
import { CAMPO_EMPRESA_LABEL, extractApiError, formatCnpj } from './helpers';
import {
  Banner,
  DialogTitle,
  FieldError,
  FieldLabel,
  MutedText,
  Overlay,
  PillButton,
  TextInput,
} from './index.style';
import { Drawer, DrawerFooter, Select } from './NewEmitterDrawer.style';
import { FieldsRow } from './NumberingModal.style';
import { numeracaoSchema } from './NumberingModal';
import { CandidataEmissor, EmissorPainel } from './types';

const novoEmissorSchema = z.object({
  companyId: z.coerce.number({ invalid_type_error: 'Escolha a empresa.' }).int().min(1, 'Escolha a empresa.'),
  homologacao: numeracaoSchema,
});

type NovoEmissorForm = z.infer<typeof novoEmissorSchema>;

interface NewEmitterDrawerProps {
  onClose: () => void;
  onCreated: (emissor: EmissorPainel, vinculado: boolean) => Promise<void>;
}

const NewEmitterDrawer = ({ onClose, onCreated }: NewEmitterDrawerProps) => {
  const [candidatas, setCandidatas] = useState<CandidataEmissor[] | null>(null);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    resetField,
    formState: { errors, isSubmitting },
  } = useForm<NovoEmissorForm>({
    resolver: zodResolver(novoEmissorSchema),
    defaultValues: { companyId: 0, homologacao: { serieNFe: '1', sequencialNFe: 1, sequencialLoteNFe: 1 } },
  });

  useEffect(() => {
    const controller = new AbortController();
    ApiService.fiscal
      .getEmissoresCandidatos()
      .then(({ data }) => {
        if (!controller.signal.aborted) setCandidatas(data);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setErroEnvio(extractApiError(error, 'Não foi possível carregar as empresas.').message);
      });
    return () => controller.abort();
  }, []);

  const companyId = Number(watch('companyId'));

  useEffect(() => {
    resetField('homologacao');
  }, [companyId, resetField]);
  const escolhida = candidatas?.find((c) => c.companyId === companyId);
  const vincula = escolhida?.noGateway === true;

  const onSubmit = async (values: NovoEmissorForm) => {
    setErroEnvio(null);
    try {
      const { data } = await ApiService.fiscal.createEmissor({
        companyId: values.companyId,
        ...(vincula ? {} : { homologacao: values.homologacao }),
      });
      await onCreated(data, data.vinculado);
    } catch (error) {
      const { message, campos } = extractApiError(error, 'Não foi possível criar o emissor.');
      const faltando = campos.map((c) => CAMPO_EMPRESA_LABEL[c] ?? c).join(', ');
      setErroEnvio(faltando ? `${message} Falta: ${faltando}.` : message);
    }
  };

  return ReactDOM.createPortal(
    <Overlay $align='right' onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>Novo emissor</DialogTitle>

        <FieldLabel>
          Empresa
          <Select {...register('companyId')} disabled={!candidatas}>
            <option value={0}>{candidatas ? 'Escolha uma empresa da conta' : 'Carregando…'}</option>
            {candidatas?.map((c) => (
              <option key={c.companyId} value={c.companyId}>
                {c.companyName ?? `Empresa ${c.companyId}`} · {formatCnpj(c.cnpj)}
              </option>
            ))}
          </Select>
          {errors.companyId && <FieldError>{errors.companyId.message}</FieldError>}
        </FieldLabel>
        {candidatas?.length === 0 && (
          <MutedText>Todas as empresas próprias da conta já são emissores.</MutedText>
        )}

        {escolhida && vincula && (
          <Banner $tone='info'>
            Esta empresa já está cadastrada no Nota Gateway. O emissor será vinculado ao cadastro existente, com a
            numeração e o certificado de lá.
          </Banner>
        )}

        {escolhida && !vincula && (
          <>
            <Banner $tone={escolhida.noGateway === null ? 'warn' : 'info'}>
              {escolhida.noGateway === null
                ? 'Não foi possível consultar o Nota Gateway agora. Se o CNPJ já existir lá, o emissor será vinculado.'
                : 'Empresa nova no Nota Gateway: ela será cadastrada com os dados da empresa. Defina a numeração de homologação.'}
            </Banner>
            <FieldsRow>
              <FieldLabel>
                Série
                <TextInput {...register('homologacao.serieNFe')} />
                {errors.homologacao?.serieNFe && <FieldError>{errors.homologacao.serieNFe.message}</FieldError>}
              </FieldLabel>
              <FieldLabel>
                Nº inicial
                <TextInput type='number' min={1} {...register('homologacao.sequencialNFe')} />
                {errors.homologacao?.sequencialNFe && (
                  <FieldError>{errors.homologacao.sequencialNFe.message}</FieldError>
                )}
              </FieldLabel>
              <FieldLabel>
                Lote inicial
                <TextInput type='number' min={1} {...register('homologacao.sequencialLoteNFe')} />
                {errors.homologacao?.sequencialLoteNFe && (
                  <FieldError>{errors.homologacao.sequencialLoteNFe.message}</FieldError>
                )}
              </FieldLabel>
            </FieldsRow>
            <MutedText>Produção e certificado se configuram depois, na ficha. Até o certificado subir, o emissor fica pendente.</MutedText>
          </>
        )}

        {erroEnvio && <Banner $tone='bad'>{erroEnvio}</Banner>}

        <DrawerFooter>
          <PillButton type='button' onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </PillButton>
          <PillButton type='submit' $variant='primary' disabled={isSubmitting || !escolhida}>
            {isSubmitting ? 'Salvando…' : vincula ? 'Vincular emissor' : 'Criar emissor'}
          </PillButton>
        </DrawerFooter>
      </Drawer>
    </Overlay>,
    document.body,
  );
};

export default NewEmitterDrawer;
